import Papa from 'papaparse';

/**
 * @param {Record<string, unknown>} block
 * @returns {block is { steps: unknown[] }}
 */
function hasSteps(block) {
  return (
    block != null &&
    typeof block === 'object' &&
    Array.isArray(block.steps) &&
    block.steps.length > 0
  );
}

/**
 * @param {Record<string, unknown>} step
 * @returns {unknown[]}
 */
function runoffStepRow(step) {
  return [
    step.period,
    step.cumulativeOpex,
    step.cumulativeRepaymentAssetTape,
    step.cumulativeRevenueExisting,
    step.numerator,
    step.denominator,
    step.eval,
  ];
}

const RUNOFF_STEP_HEADER = [
  'Period',
  'cumulativeOpex',
  'cumulativeRepaymentAssetTape',
  'cumulativeRevenueExisting',
  'numerator',
  'denominator',
  'eval',
];

/**
 * @param {Record<string, unknown>} step
 * @returns {unknown[]}
 */
function runwayStepRow(step) {
  return [
    step.period,
    step.cumulativeOpex,
    step.cumulativeRevenueExisting,
    step.numerator,
    step.denominator,
    step.eval,
  ];
}

const RUNWAY_STEP_HEADER = [
  'Period',
  'cumulativeOpex',
  'cumulativeRevenueExisting',
  'numerator',
  'denominator',
  'eval',
];

/**
 * Summary rows for each debug block (same fields as saved `cashflowRunoffDebug*` / `cashflowRunwayDebug`).
 * @param {Record<string, unknown>} block
 */
function summaryMatrix(block) {
  return [
    [
      'includeStartingCash',
      'startingCash',
      'finalMonthIndex',
      'salesAtCutoff',
      'requiresMoreData',
    ],
    [
      block.includeStartingCash === true,
      block.startingCash,
      block.finalMonthIndex,
      block.salesAtCutoff,
      block.requiresMoreData === true,
    ],
  ];
}

/**
 * CSV export for the expandable "Show runoff/runway calculation steps" data (saved payload).
 *
 * @param {Record<string, unknown>} payload - saved tearsheet payload (`payloadJson`)
 * @returns {string} CSV text or empty string if no debug sections
 */
export function buildRunoffRunwayDebugCsvString(payload) {
  const sections = [];

  const withCash = payload?.cashflowRunoffDebugWithCash;
  if (hasSteps(withCash)) {
    const lines = ['With starting cash'];
    lines.push(Papa.unparse(summaryMatrix(withCash)));
    lines.push(
      Papa.unparse([
        RUNOFF_STEP_HEADER,
        ...withCash.steps.map((s) => runoffStepRow(s)),
      ])
    );
    sections.push(lines.join('\n'));
  }

  const withoutCash = payload?.cashflowRunoffDebugWithoutCash;
  if (hasSteps(withoutCash)) {
    const lines = ['Revenue only (no starting cash in numerator)'];
    lines.push(Papa.unparse(summaryMatrix(withoutCash)));
    lines.push(
      Papa.unparse([
        RUNOFF_STEP_HEADER,
        ...withoutCash.steps.map((s) => runoffStepRow(s)),
      ])
    );
    sections.push(lines.join('\n'));
  }

  const runway = payload?.cashflowRunwayDebug;
  if (hasSteps(runway)) {
    const lines = ['Runway'];
    lines.push(Papa.unparse(summaryMatrix(runway)));
    lines.push(
      Papa.unparse([
        RUNWAY_STEP_HEADER,
        ...runway.steps.map((s) => runwayStepRow(s)),
      ])
    );
    sections.push(lines.join('\n'));
  }

  return sections.join('\n\n');
}
